import React from "react";
import { Balancer } from "react-wrap-balancer";
import { Text, Box, Flex, SimpleGrid, Link } from "@chakra-ui/react";
import { Section } from "components/section";
import { ArrowLink } from "ui/link";
import { type Tile, type Integration } from "lib/sanity";
import ReactMarkdown from "react-markdown";
import { MdxCode } from "components/mdx/mdx-code";
import { Card } from "./card";

type DestinationTilesProps = {
  source: Integration;
  destination: Integration;
  tiles: Tile[];
};

export function DestinationTiles({ destination, tiles }: DestinationTilesProps) {
  return (
    <Section>
      <Text as="h2" textStyle="h5" maxW="4xl">
        <Balancer>What {destination.name} objects can you sync to?</Balancer>
      </Text>

      <SimpleGrid as="ul" columns={{ base: 1, sm: 2, lg: 3 }} gap={{ base: 4, md: 7 }} mt={{ base: 10, md: 16 }}>
        {tiles.map((tile) => (
          <Card key={tile.name} as="li">
            <Box mb={8}>
              <Text as="h3" textStyle="h6" fontSize={{ base: "lg", md: "xl" }} mb={6}>
                {tile.name}
              </Text>

              <ReactMarkdown
                components={{
                  a: ({ node, href, ...props }) => <Link href={href} variant="article" target="_blank" {...props} />,
                  code: ({ node, inline, ...props }) => <MdxCode color="#a626a4" fontSize="sm" px={1} {...props} />,
                  p: ({ node, ...props }) => <Text {...props} />,
                }}
              >
                {tile.content}
              </ReactMarkdown>
            </Box>

            <Flex gap={4} flexWrap="wrap" mt="auto">
              {tile.hightouchDocsLink && (
                <ArrowLink href={tile.hightouchDocsLink} target="_blank" variant="grass">
                  Hightouch docs
                </ArrowLink>
              )}
              {tile.apiDocsLink && (
                <ArrowLink href={tile.apiDocsLink} target="_blank" rel="noopener noreferrer" variant="grass">
                  {destination.name} API
                </ArrowLink>
              )}
              {tile.customLink?.href && (
                <ArrowLink href={tile.customLink?.href} target="_blank" rel="noopener noreferrer" variant="grass">
                  {tile.customLink.text ?? "Learn more"}
                </ArrowLink>
              )}
            </Flex>
          </Card>
        ))}
      </SimpleGrid>
    </Section>
  );
}
